import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function Messages() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { threadId: paramId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(paramId || null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const listEndRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/threads");
      setThreads(data);
      if (!activeId && data.length > 0) setActiveId(data[0].id);
    } catch (err) { /* ignore */ }
  }, [activeId]);

  const loadMessages = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/threads/${id}/messages`, { params: { limit: 100 } });
      setMsgs(data);
      await api.post(`/threads/${id}/read`).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    }
  }, [t]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    if (paramId) setActiveId(paramId);
  }, [paramId]);
  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);
    const int = setInterval(() => loadMessages(activeId), 5000);
    return () => clearInterval(int);
  }, [activeId, loadMessages]);
  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async () => {
    if (!body.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`/threads/${activeId}/messages`, { body: body.trim() });
      setBody("");
      await loadMessages(activeId);
      await loadThreads();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const activeThread = threads.find((t) => t.id === activeId);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="messages-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-6">
        <h1 className="mb-4 text-2xl font-semibold tracking-tight text-slate-900">{t("messages.title")}</h1>
        <div className="grid gap-4 md:grid-cols-[320px_1fr]" style={{ minHeight: "60vh" }}>
          {/* Thread list */}
          <aside className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm" data-testid="thread-list">
            {threads.length === 0 && (
              <p className="p-4 text-center text-xs text-slate-500">{t("messages.empty_threads")}</p>
            )}
            <ul className="space-y-1">
              {threads.map((th) => (
                <li key={th.id}>
                  <button
                    onClick={() => { setActiveId(th.id); navigate(`/messages/${th.id}`); }}
                    className={
                      "w-full rounded-lg px-3 py-2 text-left transition-colors " +
                      (th.id === activeId ? "bg-indigo-50" : "hover:bg-slate-50")
                    }
                    data-testid={`thread-${th.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-slate-900">{th.counterpart_name}</span>
                      {th.unread_count > 0 && (
                        <span className="rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white" data-testid={`thread-unread-${th.id}`}>{th.unread_count}</span>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {th.kind === "submission" ? "🧩" : "📌"} {th.mission_title}
                    </p>
                    {th.last_message_preview && (
                      <p className="mt-1 truncate text-xs text-slate-500">{th.last_message_preview}</p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Thread pane */}
          <section className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="thread-pane">
            {!activeThread && <p className="my-auto text-center text-sm text-slate-500">{t("messages.pick_thread")}</p>}
            {activeThread && (
              <>
                <div className="mb-3 border-b border-slate-100 pb-3">
                  <h2 className="text-sm font-semibold text-slate-900" data-testid="thread-header-name">{activeThread.counterpart_name}</h2>
                  <p className="text-xs text-slate-500">
                    {activeThread.kind === "submission" ? t("messages.kind_submission") : t("messages.kind_mission")} · {activeThread.mission_title}
                  </p>
                </div>
                <ul className="flex-1 space-y-2 overflow-y-auto" style={{ maxHeight: "56vh" }} data-testid="message-list">
                  {msgs.length === 0 && (
                    <li className="text-center text-xs text-slate-400">{t("messages.no_messages")}</li>
                  )}
                  {msgs.map((m) => {
                    const mine = m.sender_user_id === user?.id;
                    return (
                      <li key={m.id} className={"flex " + (mine ? "justify-end" : "justify-start")} data-testid={`msg-${m.id}`}>
                        <div className={"max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm " + (mine ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-800")}>
                          <p className={"text-[10px] font-semibold uppercase tracking-wider " + (mine ? "text-indigo-100" : "text-slate-500")}>
                            {m.sender_org_name} · {m.sender_name}
                          </p>
                          <p className="mt-0.5 whitespace-pre-line">{m.body}</p>
                          <p className={"mt-1 text-right text-[10px] " + (mine ? "text-indigo-100" : "text-slate-400")}>{fmtTime(m.created_at)}</p>
                        </div>
                      </li>
                    );
                  })}
                  <li ref={listEndRef} />
                </ul>
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value.slice(0, 5000))}
                    onKeyDown={onKey}
                    rows={2}
                    placeholder={t("messages.input_placeholder")}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    data-testid="message-input"
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-[10px] text-slate-400">{body.length}/5000 · {t("messages.hint")}</p>
                    <button onClick={send} disabled={sending || !body.trim()} className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60" data-testid="message-send">
                      {sending ? t("common.loading") : t("messages.send")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
